'use client'

import { useState, useEffect, useRef } from 'react'
import { Dialog } from '@/components/ui/dialog'

interface ForkDialogProps {
  isOpen: boolean
  promptTitle: string
  authorUsername: string
  error?: string | null
  onFork: () => Promise<void>
  onCancel: () => void
}

export function ForkDialog({
  isOpen,
  promptTitle,
  authorUsername,
  error,
  onFork,
  onCancel,
}: ForkDialogProps) {
  const [isForking, setIsForking] = useState(false)
  const firstFocusRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isOpen) firstFocusRef.current?.focus()
  }, [isOpen])

  const handleFork = async () => {
    setIsForking(true)
    try {
      await onFork()
    } finally {
      setIsForking(false)
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      titleId="fork-dialog-title"
      busy={isForking}
    >
      <h2
        id="fork-dialog-title"
        className="text-[var(--text-xl)] font-semibold text-[var(--color-text-primary)]"
      >
        Fork This Prompt
      </h2>
      <p className="mt-1 text-[var(--text-sm)] text-[var(--color-text-secondary)]">
        Make your own editable copy of @{authorUsername}&apos;s prompt.
      </p>

      {/* Info - describes the actual server behavior: the copy keeps the
          original title and is created Private. (The old literal "- 2" was
          wrong: the server only suffixes " (2)" on a title collision.) */}
      <div className="mt-4 rounded-[var(--radius-md)] bg-[var(--color-bg-primary)] px-3 py-2 text-[var(--text-xs)] text-[var(--color-text-muted)]">
        Your copy will be named &ldquo;{promptTitle}&rdquo; and set to Private (only you can see it). You can rename and publish it afterwards.
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-4 rounded-[var(--radius-md)] border border-red-300 bg-red-50 px-3 py-2 text-[var(--text-sm)] text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="mt-5 flex items-center justify-end gap-3">
        <button
          ref={firstFocusRef}
          type="button"
          onClick={onCancel}
          disabled={isForking}
          className="rounded-[var(--radius-md)] px-4 py-2 text-[var(--text-sm)] text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleFork}
          disabled={isForking}
          className="flex items-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-4 py-2 text-[var(--text-sm)] font-medium text-white transition-colors hover:bg-[var(--color-brand-violet)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)] disabled:opacity-50"
        >
          {isForking && (
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
          Fork
        </button>
      </div>
    </Dialog>
  )
}
